import { supabase } from '../lib/supabase';

export const complianceService = {
  // ==================== COMPLIANCE POLICIES ====================
  
  async getPolicies(organizationId) {
    try {
      const { data, error } = await supabase
        ?.from('compliance_policies')
        ?.select(`
          *,
          creator:user_profiles!compliance_policies_created_by_fkey(id, email, full_name)
        `)
        ?.eq('organization_id', organizationId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getPolicyByType(organizationId, policyType) {
    try {
      const { data, error } = await supabase
        ?.from('compliance_policies')
        ?.select('*')
        ?.eq('organization_id', organizationId)
        ?.eq('policy_type', policyType)
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createPolicy(organizationId, policyData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.from('compliance_policies')
        ?.insert({
          organization_id: organizationId,
          policy_type: policyData?.policyType,
          policy_name: policyData?.policyName,
          policy_config: policyData?.policyConfig,
          enforcement_status: policyData?.enforcementStatus || 'active',
          created_by: user?.id
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updatePolicy(policyId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('compliance_policies')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', policyId)
        ?.select()
        ?.single();

      if (error) throw error;
      
      // Trigger security alert for compliance policy changes
      if (data && updates?.enforcement_status === 'violated') {
        try {
          const { default: securityAlertService } = await import('./securityAlertService');
          await securityAlertService?.checkPolicyViolation('compliance', {
            policyId,
            policyType: data?.policy_type,
            policyName: data?.policy_name,
            description: `Compliance policy "${data?.policy_name}" has been violated`
          });
        } catch (alertError) {
          console.error('Error triggering compliance alert:', alertError);
        }
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deletePolicy(policyId) {
    try {
      const { error } = await supabase
        ?.from('compliance_policies')
        ?.delete()
        ?.eq('id', policyId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // ==================== INACTIVE MEMBER DETECTION ====================
  
  async getInactiveMembers(organizationId, thresholdDays = 30) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_inactive_members', {
          p_organization_id: organizationId,
          p_threshold_days: thresholdDays
        });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async getInactiveMembersByThresholds(organizationId) {
    try {
      const [days30, days60, days90] = await Promise?.all([
        this?.getInactiveMembers(organizationId, 30),
        this?.getInactiveMembers(organizationId, 60),
        this?.getInactiveMembers(organizationId, 90)
      ]);

      return {
        data: {
          thirtyDays: days30?.data || [],
          sixtyDays: days60?.data || [],
          ninetyDays: days90?.data || []
        },
        error: null
      };
    } catch (error) {
      return {
        data: { thirtyDays: [], sixtyDays: [], ninetyDays: [] },
        error
      };
    }
  },

  // ==================== PERMISSION AUDIT LOG ====================
  
  async getPermissionAuditLog(organizationId, filters = {}) {
    try {
      const {
        changeType,
        memberId,
        startDate,
        endDate,
        page = 1,
        pageSize = 20
      } = filters;

      let query = supabase
        ?.from('permission_audit_log')
        ?.select(`
          *,
          team_member:team_members!permission_audit_log_team_member_id_fkey(
            id,
            role,
            user:user_profiles(id, email, full_name)
          ),
          changed_by_user:user_profiles!permission_audit_log_changed_by_fkey(id, email, full_name)
        `, { count: 'exact' })
        ?.eq('organization_id', organizationId);

      // Apply filters
      if (changeType) {
        query = query?.eq('change_type', changeType);
      }

      if (memberId) {
        query = query?.eq('team_member_id', memberId);
      }

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      // Pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query?.range(from, to);

      // Order by most recent first
      query = query?.order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        error: null,
        pagination: {
          page,
          pageSize,
          total: count || 0,
          totalPages: Math?.ceil((count || 0) / pageSize)
        }
      };
    } catch (error) {
      return { data: [], error, pagination: null };
    }
  },

  async getEscalationHistory(organizationId) {
    try {
      const { data, error } = await supabase
        ?.from('permission_audit_log')
        ?.select(`
          *,
          team_member:team_members!permission_audit_log_team_member_id_fkey(
            id,
            role,
            user:user_profiles(id, email, full_name)
          ),
          changed_by_user:user_profiles!permission_audit_log_changed_by_fkey(id, email, full_name)
        `)
        ?.eq('organization_id', organizationId)
        ?.eq('change_type', 'escalation')
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async exportAuditLog(organizationId, filters = {}) {
    try {
      const { changeType, startDate, endDate } = filters;

      let query = supabase
        ?.from('permission_audit_log')
        ?.select(`
          *,
          team_member:team_members!permission_audit_log_team_member_id_fkey(
            user:user_profiles(email, full_name)
          ),
          changed_by_user:user_profiles!permission_audit_log_changed_by_fkey(email, full_name)
        `)
        ?.eq('organization_id', organizationId);

      if (changeType) {
        query = query?.eq('change_type', changeType);
      }

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Convert to CSV
      const csv = this?.convertAuditLogToCSV(data || []);
      return { data: csv, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  convertAuditLogToCSV(logs) {
    if (!logs || logs?.length === 0) return '';

    const headers = ['Timestamp', 'Member', 'Changed By', 'Change Type', 'Previous Role', 'New Role', 'Reason', 'IP Address'];
    const rows = logs?.map((log) => [
      new Date(log?.created_at)?.toLocaleString(),
      log?.team_member?.user?.full_name || log?.team_member?.user?.email || 'Unknown',
      log?.changed_by_user?.full_name || log?.changed_by_user?.email || 'System',
      log?.change_type?.replace('_', ' ')?.toUpperCase(),
      log?.previous_role || 'N/A',
      log?.new_role || 'N/A',
      log?.change_reason || 'N/A',
      log?.ip_address || 'N/A'
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map((row) => row?.map((cell) => `"${cell}"`)?.join(','))
    ]?.join('\n');

    return csvContent;
  },

  downloadCSV(csvContent, filename = 'permission-audit-log.csv') {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document?.createElement('a');
    const url = URL?.createObjectURL(blob);

    link?.setAttribute('href', url);
    link?.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document?.body?.appendChild(link);
    link?.click();
    document?.body?.removeChild(link);
  },

  // ==================== STATISTICS ====================
  
  async getComplianceStatistics(organizationId) {
    try {
      const [policiesResult, inactiveResult, auditResult] = await Promise?.all([
        this?.getPolicies(organizationId),
        this?.getInactiveMembersByThresholds(organizationId),
        this?.getPermissionAuditLog(organizationId, { page: 1, pageSize: 1 })
      ]);

      const activePolicies = policiesResult?.data?.filter(p => p?.enforcement_status === 'active')?.length || 0;
      const totalInactive = (
        (inactiveResult?.data?.thirtyDays?.length || 0) +
        (inactiveResult?.data?.sixtyDays?.length || 0) +
        (inactiveResult?.data?.ninetyDays?.length || 0)
      );
      const totalAuditLogs = auditResult?.pagination?.total || 0;

      return {
        data: {
          activePolicies,
          totalInactiveMembers: totalInactive,
          totalAuditLogs,
          inactiveBreakdown: {
            thirtyDays: inactiveResult?.data?.thirtyDays?.length || 0,
            sixtyDays: inactiveResult?.data?.sixtyDays?.length || 0,
            ninetyDays: inactiveResult?.data?.ninetyDays?.length || 0
          }
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  }
};